import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Company, CreateCompanyRequest, UpdateCompanyRequest } from '../models/company.model';
import { COMPANY_SEARCH_LIMIT } from '../utils/company-search';
import { resolveCompanyDownloadFilename } from '../utils/company-download-filename';

export interface CompanyFilter {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: number;
  is_active?: boolean;
}

/** Filtros alineados con bardelucas empresas/lista. */
export interface CompanyListFilters {
  page?: number;
  limit?: number;
  q?: string;
  o?: string;
  q_estado?: string;
  b_trial?: string;
  q_pais?: string;
  id?: string;
  id_holding?: string;
  id_kam?: string;
  id_cs?: string;
  clasificacion?: string;
  tipo_venta?: string;
  created_at_from?: string;
  created_at_to?: string;
  fecha_proxima_facturacion_from?: string;
  fecha_proxima_facturacion_to?: string;
}

export interface CompanyListRow {
  id: string;
  name: string | null;
  countryName: string | null;
  createdAt: string | null;
  activeUsersTotal: number;
  activeUsers30d: number;
  activeUsersPercent: number | null;
  isActive: boolean;
  suspended?: boolean;
  isDebtor: boolean;
  isTrial: boolean;
  planCode: string | null;
  planCurrency: string | null;
  hasPlan: boolean;
  nextBillingDate: string | null;
  tipoVenta: number | null;
  hubspotId: string | null;
  holdingId: string | null;
  clasification: number | null;
  clasificationLabel: string | null;
  countryLabel: string | null;
}

export interface CompanyListResponse {
  data: CompanyListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CompanyListCatalogOption {
  id: string;
  label: string;
}

export interface CompanyListCatalogs {
  paises: { id: string; nombre: string }[];
  teamUsers: CompanyListCatalogOption[];
  orderOptions: CompanyListCatalogOption[];
  trialOptions: CompanyListCatalogOption[];
  statusOptions: CompanyListCatalogOption[];
  clasificacionOptions: CompanyListCatalogOption[];
  tipoVentaOptions: CompanyListCatalogOption[];
}

export interface CompanyResponse {
  companies: Company[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface CompanyPrebillFileItem {
  storagePath: string;
  fileName: string;
  imageUrl: string | null;
}

export interface CompanyPrebillAttachment {
  prebillId: string;
  prebillDate: string | null;
  startPaymentPeriod: string | null;
  endPaymentPeriod: string | null;
  files: CompanyPrebillFileItem[];
}

export interface CompanyEmployeeItem {
  userId: string;
  userCompanyId: string;
  username: string | null;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  department: string | null;
  lastAccess: string | null;
  isUserActive: boolean;
  isCompanyUserActive: boolean;
  isAdmin: boolean;
  isApprover: boolean;
  isSuperAdmin: boolean;
  isContentManager: boolean;
  isIosApp: boolean;
  isAndroidApp: boolean;
  isInternalRindegastos: boolean;
  roleLabels: string[];
}

export interface CompanyEmployeesResponse {
  data: CompanyEmployeeItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CompanyContractItem {
  fileName: string;
  url: string;
}

export interface CompanyDashboardResponse {
  companyId: number;
  startDate: string;
  endDate: string;
  groupBy: number;
  totals: {
    expensesAllTime: number;
    reportsAllTime: number;
    expensesPerReport: number | null;
  };
  users: {
    activeTotal: number;
    activeCurrentMonth: number;
    inactiveCurrentMonth: number;
  };
  series: Array<{ label: string; expenses: number; reports: number }>;
}

/**
 * Un mes del desglose de informes y gastos.
 * `month` es `YYYY-MM`. `reports` e `expenses` son conteos (0 si el mes no tuvo movimiento).
 */
export interface CompanyMonthlyStatsPoint {
  month: string;
  reports: number;
  expenses: number;
}

/**
 * Tramo después de `/companies/{id}`. El id va en el path.
 *
 * GET {apiUrl}/companies/{id}/stats/monthly?from=YYYY-MM&to=YYYY-MM
 * `from` y `to` son opcionales. Sin ellos el back responde los últimos 12 meses,
 * ascendente, con los meses vacíos ya en 0. El front no rellena huecos.
 * 200: CompanyMonthlyStatsPoint[]
 * 400: formato inválido, from > to, o rango mayor a 36 meses.
 * 404: la empresa no existe.
 */
export const COMPANY_MONTHLY_STATS_PATH = '/stats/monthly';

export interface RindeconnectConfig {
  isRindeconnectActive: boolean;
  showRindeconnect: boolean;
}

/** Un cliente tal como está hoy en Alegra México. */
export interface AlegraMexicoContactSnapshot {
  id: string;
  identification: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  municipality: string | null;
  zipCode: string | null;
  state: string | null;
  regime: string | null;
  /** Método del SAT: `PUE`/`PPD`. En la ficha se llama "método de pago". */
  paymentType: string | null;
  /** Forma de pago de Alegra (`other`, `transfer`…). En la ficha, "forma de pago". */
  paymentMethod: string | null;
  cfdiUse: string | null;
}

export interface MexicoLookupResult {
  rfcConsultado: string;
  /** Cuenta consultada. La ficha siempre pide `prod`. */
  ambiente: 'prod' | 'sandbox';
  encontrado: boolean;
  cliente: AlegraMexicoContactSnapshot | null;
  /** `id-ficha` = no apareció por RFC y se recuperó por el id guardado. */
  resueltoPor: 'rfc' | 'id-ficha' | null;
  /** El RFC del contacto en Alegra no es el de la ficha: el CFDI sale a otro nombre. */
  rfcDiscrepante: boolean;
  /** Otros contactos con el mismo RFC: facturas repartidas entre varios clientes. */
  posiblesDuplicados: AlegraMexicoContactSnapshot[];
  /** El `alegra_id_cliente` de la ficha apunta a un contacto que no es el del RFC. */
  idClienteDiscrepante: boolean;
  idClienteFicha: string | null;
  mensaje: string | null;
}

/**
 * De dónde salieron los datos. SII, ARCA y SUNAT no tienen cuenta de pruebas:
 * lo que puede variar es contra qué instancia de `metadata` pega el back, y eso
 * es lo que se informa para poder mostrarlo en la ficha.
 */
export interface FiscalSourceInfo {
  ambiente: 'prod' | 'no-prod';
  /** Host de metadata que respondió. */
  fuente: string;
}

export interface SiiValidationResult extends FiscalSourceInfo {
  rut: string;
  razon_social: string;
  giro: string;
  fiscalInfo: any;
}

export interface ArcaValidationResult extends FiscalSourceInfo {
  cuit: string;
  razon_social: string;
  domicilio: string;
  actividad: string;
  tipo_persona: string;
  estado: string;
  fiscalInfo: any;
}

export interface SunatValidationResult extends FiscalSourceInfo {
  ruc: string;
  razon_social: string;
  giro: string;
  direccion: string;
  distrito: string;
  provincia: string;
  departamento: string;
  ubigeo: string;
  estado: string;
  condicion: string;
  fiscalInfo: any;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private apiUrl = `${environment.apiUrl}/companies`;

  constructor(private http: HttpClient) {}

  getCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.apiUrl}/all`);
  }

  getCompaniesPaginated(filters: CompanyFilter): Observable<CompanyResponse> {
    let params = new HttpParams();

    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.limit) params = params.set('limit', filters.limit.toString());
    if (filters.search) params = params.set('search', filters.search);
    if (filters.companyId) params = params.set('companyId', filters.companyId.toString());
    if (filters.is_active !== undefined) params = params.set('is_active', filters.is_active.toString());

    return this.http.get<CompanyResponse>(this.apiUrl, { params });
  }

  getCompanyList(filters: CompanyListFilters): Observable<CompanyListResponse> {
    let params = new HttpParams();
    const entries = Object.entries(filters) as [keyof CompanyListFilters, string | number | undefined][];
    for (const [key, value] of entries) {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(String(key), String(value));
      }
    }
    return this.http.get<CompanyListResponse>(`${this.apiUrl}/list`, { params });
  }

  getCompanyListCatalogs(): Observable<CompanyListCatalogs> {
    return this.http.get<CompanyListCatalogs>(`${this.apiUrl}/list/catalogs`);
  }

  getCompany(id: number): Observable<Company> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<Company>(`${this.apiUrl}`, { params });
  }

  /** Búsqueda para autocompletado (nombre, mail, RUT o id si es numérico). */
  searchCompanies(query: string, limit = COMPANY_SEARCH_LIMIT): Observable<Company[]> {
    const q = query.trim();
    let params = new HttpParams().set('limit', String(limit)).set('page', '1');
    if (q) {
      params = params.set('search', q);
    }
    return this.http.get<Company[] | Company>(this.apiUrl, { params }).pipe(
      map((res) => {
        if (Array.isArray(res)) {
          return res;
        }
        return res ? [res] : [];
      }),
    );
  }

  createCompany(company: CreateCompanyRequest): Observable<Company> {
    return this.http.post<Company>(this.apiUrl, company);
  }

  updateCompany(id: number, company: UpdateCompanyRequest): Observable<Company> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.patch<Company>(this.apiUrl, company, { params });
  }

  deleteCompany(id: number): Observable<void> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.delete<void>(this.apiUrl, { params });
  }

  /**
   * Consulta el RFC en Alegra México. No hay organismo fiscal que consultar: el
   * SAT no publica datos por RFC, así que la fuente es el sistema emisor.
   */
  lookupMexicoAlegra(id: number): Observable<MexicoLookupResult> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<MexicoLookupResult>(`${this.apiUrl}/mexico-billing/lookup`, { params });
  }

  getCompanyUsers(id: number): Observable<any[]> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<any[]>(`${this.apiUrl}/users`, { params });
  }

  getCompanyEmployees(
    companyId: number,
    options?: { q?: string; page?: number; limit?: number },
  ): Observable<CompanyEmployeesResponse> {
    let params = new HttpParams().set('id', companyId.toString());
    if (options?.q) {
      params = params.set('q', options.q);
    }
    if (options?.page != null) {
      params = params.set('page', String(options.page));
    }
    if (options?.limit != null) {
      params = params.set('limit', String(options.limit));
    }
    return this.http.get<CompanyEmployeesResponse>(`${this.apiUrl}/employees`, { params });
  }

  getCompanyPrebillAttachments(companyId: number): Observable<CompanyPrebillAttachment[]> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.get<CompanyPrebillAttachment[]>(`${this.apiUrl}/prebill-attachments`, { params });
  }

  getCiudadesColombia(): Observable<{ nombre: string; cityCode: string }[]> {
    return this.http.get<{ nombre: string; cityCode: string }[]>(`${this.apiUrl}/ciudades-colombia`);
  }

  /** Valida un RUT chileno contra el SII (vía metadata) y devuelve razón social + giro. */
  validateSii(rut: string): Observable<SiiValidationResult> {
    const params = new HttpParams().set('rut', rut);
    return this.http.get<SiiValidationResult>(`${this.apiUrl}/validate-sii`, { params });
  }

  validateArca(cuit: string): Observable<ArcaValidationResult> {
    const params = new HttpParams().set('cuit', cuit);
    return this.http.get<ArcaValidationResult>(`${this.apiUrl}/validate-arca`, { params });
  }

  /** Valida un RUC peruano contra SUNAT (vía metadata) y devuelve la ficha del contribuyente. */
  validateSunat(ruc: string): Observable<SunatValidationResult> {
    const params = new HttpParams().set('ruc', ruc);
    return this.http.get<SunatValidationResult>(`${this.apiUrl}/validate-sunat`, { params });
  }

  getTrialCompanies(page: number = 1, limit: number = 30, q?: string, o?: string, q_estado: number = -2, q_pais: number = 0): Observable<any> {
    let params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('q_estado', q_estado)
      .set('q_pais', q_pais);
    if (q) params = params.set('q', q);
    if (o) params = params.set('o', o);
    return this.http.get<any>(`${this.apiUrl}/trial`, { params });
  }

  getLogUsuarios(companyId: number, from?: string, to?: string): Observable<any[]> {
    let params = new HttpParams().set('e', companyId);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<any[]>(`${this.apiUrl}/log-usuarios`, { params });
  }

  generateLogUsuarios(companyId: number, desde: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/log-usuarios/generate`, { e: companyId, desde });
  }

  getCompanyPlan(id: number): Observable<any> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<any>(`${this.apiUrl}/plan`, { params });
  }

  getHoldingCompanies(companyId: number): Observable<{ id: string; name: string | null; isActive: boolean }[]> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.get<{ id: string; name: string | null; isActive: boolean }[]>(`${this.apiUrl}/holding`, { params });
  }

  updateCompanyPlan(id: number, planData: any): Observable<any> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.put<any>(`${this.apiUrl}/plan`, planData, { params });
  }

  getCompanyDashboard(
    companyId: number,
    filters: { inicio?: string; termino?: string; agrupar_por?: number },
  ): Observable<CompanyDashboardResponse> {
    let params = new HttpParams().set('id', companyId.toString());
    if (filters.inicio) {
      params = params.set('inicio', filters.inicio);
    }
    if (filters.termino) {
      params = params.set('termino', filters.termino);
    }
    if (filters.agrupar_por != null) {
      params = params.set('agrupar_por', String(filters.agrupar_por));
    }
    return this.http.get<CompanyDashboardResponse>(`${this.apiUrl}/dashboard`, { params });
  }

  getCompanyMonthlyStats(
    companyId: number,
    range?: { from?: string; to?: string },
  ): Observable<CompanyMonthlyStatsPoint[]> {
    let params = new HttpParams();
    if (range?.from) {
      params = params.set('from', range.from);
    }
    if (range?.to) {
      params = params.set('to', range.to);
    }
    return this.http.get<CompanyMonthlyStatsPoint[]>(
      `${this.apiUrl}/${encodeURIComponent(String(companyId))}${COMPANY_MONTHLY_STATS_PATH}`,
      { params },
    );
  }

  getCompanyContracts(companyId: number): Observable<CompanyContractItem[]> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.get<CompanyContractItem[]>(`${this.apiUrl}/contracts`, { params });
  }

  uploadCompanyContract(companyId: number, file: File): Observable<CompanyContractItem[]> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<CompanyContractItem[]>(`${this.apiUrl}/contracts`, formData, { params });
  }

  deleteCompanyContract(companyId: number, fileName: string): Observable<CompanyContractItem[]> {
    const params = new HttpParams().set('id', companyId.toString()).set('fileName', fileName);
    return this.http.delete<CompanyContractItem[]>(`${this.apiUrl}/contracts`, { params });
  }

  getRindeconnectConfig(companyId: number): Observable<RindeconnectConfig> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.get<RindeconnectConfig>(`${this.apiUrl}/rindeconnect/config`, { params });
  }

  enableRindeconnect(companyId: number, payload: { provider: string; bank?: string | null }): Observable<any> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post(`${this.apiUrl}/rindeconnect/enable`, payload, { params });
  }

  createIntercomUsers(companyId: number): Observable<any> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post(`${this.apiUrl}/intercom/create-users`, {}, { params });
  }

  /** Links an existing @rindegastos.com user to the company. */
  addInternalUser(
    companyId: number,
    email: string,
    asAdmin = true,
  ): Observable<{
    userId: string;
    email: string;
    companyId: string;
    relationId: string;
    created: boolean;
    reactivated: boolean;
    asAdmin: boolean;
  }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<{
      userId: string;
      email: string;
      companyId: string;
      relationId: string;
      created: boolean;
      reactivated: boolean;
      asAdmin: boolean;
    }>(`${this.apiUrl}/users/add-internal`, { email, asAdmin }, { params });
  }

  listInternalUsers(companyId: number): Observable<{
    userId: string;
    email: string;
    fullName: string | null;
    isActive: boolean;
  }[]> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.get<{
      userId: string;
      email: string;
      fullName: string | null;
      isActive: boolean;
    }[]>(`${this.apiUrl}/users/internal`, { params });
  }

  removeInternalUser(companyId: number, email: string): Observable<{
    userId: string;
    email: string;
    companyId: string;
    removed: boolean;
  }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.delete<{
      userId: string;
      email: string;
      companyId: string;
      removed: boolean;
    }>(`${this.apiUrl}/users/remove-internal`, { params, body: { email } });
  }

  disableCompanyNewsletters(companyId: number): Observable<{ updatedCount: number }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<{ updatedCount: number }>(`${this.apiUrl}/users/disable-newsletters`, {}, { params });
  }

  enableCompanyNewsletters(companyId: number): Observable<{ updatedCount: number }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<{ updatedCount: number }>(`${this.apiUrl}/users/enable-newsletters`, {}, { params });
  }

  enableCompanyAutomaticEmails(companyId: number): Observable<{ updatedCount: number }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<{ updatedCount: number }>(`${this.apiUrl}/users/enable-automatic-emails`, {}, { params });
  }

  disableCompanyAutomaticEmails(companyId: number): Observable<{ updatedCount: number }> {
    const params = new HttpParams().set('id', companyId.toString());
    return this.http.post<{ updatedCount: number }>(`${this.apiUrl}/users/disable-automatic-emails`, {}, { params });
  }

  // ─── Descargas Excel ────────────────────────────────────────────────────────

  private readonly downloadBase = `${environment.apiUrl}/downloads/company`;

  downloadUsers(companyId: number): Observable<{ blob: Blob; filename: string }> {
    return this.http.get(`${this.downloadBase}/users`, {
      params: new HttpParams().set('id', companyId),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: this.extractFilename(res.headers.get('content-disposition'), 'ListadoUsuarios.xlsx'),
      }))
    );
  }

  downloadConfigOption(
    companyId: number,
    option: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
    companyName?: string,
  ): Observable<{ blob: Blob; filename: string }> {
    return this.http.get(`${this.downloadBase}/config-options`, {
      params: new HttpParams().set('id', companyId).set('option', option),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: this.filenameFromDownload(res, option, companyId, companyName),
      }))
    );
  }

  downloadCompanyWorkflows(companyId: number, companyName?: string): Observable<{ blob: Blob; filename: string }> {
    return this.downloadConfigOption(companyId, 6, companyName);
  }

  downloadWorkflows(companyId: number, companyName?: string): Observable<{ blob: Blob; filename: string }> {
    return this.downloadConfigOption(companyId, 6, companyName);
  }

  downloadBillingActivity(companyId: number, list: 1 | 2): Observable<{ blob: Blob; filename: string }> {
    return this.http.get(`${this.downloadBase}/billing-activity`, {
      params: new HttpParams().set('id', companyId).set('list', list),
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map(res => ({
        blob: res.body as Blob,
        filename: this.extractFilename(res.headers.get('content-disposition'), 'BillingActivity.xlsx'),
      }))
    );
  }

  private filenameFromDownload(
    res: { headers: { get(name: string): string | null }; body: Blob | null },
    option: number,
    companyId: number,
    companyName?: string,
  ): string {
    return resolveCompanyDownloadFilename({
      contentDisposition: res.headers.get('content-disposition'),
      contentType: res.headers.get('content-type') || res.body?.type || null,
      option,
      companyId,
      companyName,
    });
  }

  private extractFilename(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) return fallback;
    const match = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?["']?([^;"'\n]+)/i)
      ?? contentDisposition.match(/filename\s*=\s*["']?([^;"'\n]+)/i);
    if (!match) return fallback;
    try {
      return decodeURIComponent(match[1].trim());
    } catch {
      return match[1].trim();
    }
  }

  triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}